import { useRef, useState } from 'react'
import {
  BACKGROUND_PRESETS,
  fontStack,
  isLightBackground,
  PALETTE_SLOTS,
  type OrgDesign,
  type OrgDto,
} from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { ColorPicker } from './ColorPicker.js'
import { Dialog } from './Dialog.js'
import { useText } from '../i18n.js'
import { FontLibrary, FontSelect, useOrgFonts } from './FontLibrary.js'

/** Platz der Akzentfarbe in der Palette — dieselbe Stelle wie im Anlege-Fenster. */
const ACCENT_SLOT = 2

/**
 * Das Standard-Design einer Organisation.
 *
 * Es beantwortet die Frage „womit fängt ein neues Board an" ein für alle Mal,
 * statt sie jedem Mitglied jedes Mal neu zu stellen. Bestehende Boards rührt es
 * nicht an — ihre Gestaltung ist Inhalt und liegt im Board selbst.
 */
export function OrgDesignDialog({
  org,
  onClose,
  onSaved,
}: {
  org: OrgDto
  onClose: () => void
  onSaved: () => void
}): React.ReactElement {
  const t = useText()
  const [design, setDesign] = useState<OrgDesign>(org.design)
  const [fonts, setFonts] = useOrgFonts(org.id)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [logo, setLogo] = useState<string | null>(org.logoUrl)
  const logoInput = useRef<HTMLInputElement>(null)

  async function uploadLogo(file: File): Promise<void> {
    setError(null)
    try {
      await api.setOrgLogo(org.id, file)
      // Zeitstempel an die Adresse, sonst zeigt der Browser das alte Bild.
      setLogo(`/api/orgs/${org.id}/logo?v=${Date.now()}`)
      onSaved()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('design.logoFailed'))
    }
  }

  async function removeLogo(): Promise<void> {
    setError(null)
    try {
      await api.removeOrgLogo(org.id)
      setLogo(null)
      onSaved()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('design.logoRemoveFailed'))
    }
  }

  const isAdmin = org.role === 'admin'
  const light = isLightBackground(design.background)
  const slots = Array.from(
    { length: PALETTE_SLOTS },
    (_, index) => design.theme.palette[index] ?? '#888888',
  )

  const change = (next: OrgDesign): void => {
    setDesign(next)
    setSaved(false)
  }

  const setSlot = (index: number, color: string): void => {
    const palette = [...slots]
    palette[index] = color
    change({ ...design, theme: { ...design.theme, palette } })
  }

  async function save(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      // Die Palette wird auf volle Länge gebracht: Ein Design mit fünf Farben
      // weist der Server ab, und beim Anlegen käme es ohnehin nie vor.
      await api.setOrgDesign(org.id, { ...design, theme: { ...design.theme, palette: slots } })
      setSaved(true)
      onSaved()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('design.saveFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog title={t('design.title', { org: org.name })} onClose={onClose}>
      <p className="muted small">{t('design.lead')}</p>

      <fieldset className="plain" disabled={!isAdmin}>
        <div className="field">
          <span>{t('theme.backgroundLabel')}</span>
          <span className="swatch-row">
            {BACKGROUND_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className={`swatch${design.background === preset.value ? ' is-selected' : ''}`}
                style={{ backgroundColor: preset.value }}
                title={t(preset.name)}
                aria-label={t(preset.name)}
                aria-pressed={design.background === preset.value}
                onClick={() => change({ ...design, background: preset.value })}
              />
            ))}
            <ColorPicker
              value={design.background}
              onChange={(background) => change({ ...design, background })}
              palette={slots}
              title={t('color.custom')}
            />
          </span>
        </div>

        <div className="field">
          <span>{t('theme.palette')}</span>
          <span className="theme-row">
            {slots.map((color, index) => (
              <ColorPicker
                key={index}
                value={color}
                onChange={(next) => setSlot(index, next)}
                title={
                  index === ACCENT_SLOT
                    ? t('newBoard.accent')
                    : t('theme.color', { number: index + 1 })
                }
              />
            ))}
          </span>
        </div>

        <FontSelect
          label={t('theme.headings')}
          value={design.theme.headingFont}
          fonts={fonts}
          onChange={(headingFont) => change({ ...design, theme: { ...design.theme, headingFont } })}
        />
        <FontSelect
          label={t('theme.body')}
          value={design.theme.bodyFont}
          fonts={fonts}
          onChange={(bodyFont) => change({ ...design, theme: { ...design.theme, bodyFont } })}
        />
      </fieldset>

      <div
        className={`board-preview${light ? ' is-light' : ''}`}
        style={{ backgroundColor: design.background }}
        aria-hidden="true"
      >
        <span
          className="board-preview-frame"
          style={{ borderColor: slots[ACCENT_SLOT] }}
        >
          <span
            className="board-preview-heading"
            style={{ fontFamily: fontStack(design.theme.headingFont) }}
          >
            {org.name}
          </span>
          <span
            className="board-preview-body"
            style={{ fontFamily: fontStack(design.theme.bodyFont) }}
          >
            {t('design.previewBody')}
          </span>
        </span>
      </div>

      {isAdmin && (
        <button className="button primary" onClick={() => void save()} disabled={busy}>
          {busy ? t('design.saving') : saved ? t('design.saved') : t('design.save')}
        </button>
      )}
      {!isAdmin && (
        <p className="muted small">{t('design.adminOnly')}</p>
      )}

      {/*
        Das Logo gehört zum Auftritt, nicht zum Board: Es erscheint auf dem
        Wartebildschirm, den geführte Gäste vor Beginn der Präsentation sehen —
        also genau dort, wo das Board noch gar nicht ausgeliefert ist.
      */}
      <h4 className="dialog-section">{t('design.logo')}</h4>
      <p className="muted small">{t('design.logoHint')}</p>
      <div className="logo-row">
        {logo ? (
          <img className="logo-preview" src={logo} alt={t('design.logoAlt', { org: org.name })} />
        ) : (
          <span className="logo-preview is-empty muted small">{t('design.noLogo')}</span>
        )}
        {isAdmin && (
          <>
            <button className="button ghost" onClick={() => logoInput.current?.click()}>
              {logo ? t('design.replace') : t('design.chooseLogo')}
            </button>
            {logo && (
              <button className="button ghost danger" onClick={() => void removeLogo()}>
                {t('common.remove')}
              </button>
            )}
            <input
              ref={logoInput}
              type="file"
              accept="image/svg+xml,image/png,image/webp"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0]
                event.target.value = ''
                if (file) void uploadLogo(file)
              }}
            />
          </>
        )}
      </div>

      <h4 className="dialog-section">{t('design.fonts')}</h4>
      <FontLibrary orgId={org.id} fonts={fonts} onChange={setFonts} canRemove={isAdmin} />

      {error && <p className="form-error">{error}</p>}
    </Dialog>
  )
}
